import {
	Field,
	Form,
	Formik,
	FormikActions,
	FormikErrors,
	FormikProps,
	FormikTouched,
} from "formik";
import Checkbox from "formik-antd/es/checkbox";
import * as React from "react";
import { useEffect, useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import LoadingOverlay from "react-loading-overlay";
import { ClipLoader } from "react-spinners";
import { injectStripe } from "react-stripe-elements";
import { isEmpty, isNullOrWhiteSpace, unCamelCase } from "../../../common/helper/helper";
import { ICampaign, message } from "../../DonateForm/Components/DonateForm";
import { stepOneCompanyFields } from "../../DonateForm/Components/Step1";
import {
	EventFormFields,
	FormConstants,
	IStandardFormProps,
	StandardFormFields,
	States,
	WaFormConstants,
} from "../../GenericForm/Components/Constants";
import ErrorMessage from "../../GenericForm/Components/ErrorMessage";
import FormButton from "../../GenericForm/Components/FormButton";
import FormField from "../../GenericForm/Components/FormField";
import FormFields from "../../GenericForm/Components/FormFields";
import {
	createGenericValidationSchema,
	removeWhiteSpaces,
} from "../../GenericForm/Components/FunctionLib";
import Modal from "../../GenericForm/Components/Modal";
import { separateCustomFormFields } from "../../GenericForm/Components/StandardForm";
import "../../GenericForm/Style/StandardForm.scss";
import { getRcTokenAsync, IRcTokenResponse, tokenFailable } from "../../GoogleReCaptcha/Recaptcha";
import { AUTOCOMPLETE_FORM_TYPE, ENTITY_TYPE, useAutoComplete } from "../../hooks/auto-complete";
import { EventRegFormModel } from "../../ml_modules/websiteApi/EventRegFormModel";
import FormCheckOutComponent from "./EventFormCheckOutComponent";

const initialValues: IInitialValues = {
	[FormConstants.firstName]: "",
	[FormConstants.lastName]: "",
	[FormConstants.email]: "",
	[FormConstants.postcode]: "",
	[FormConstants.numberOfTickets]: 1,
	[WaFormConstants.entityType]: ENTITY_TYPE.Individual,
	[FormConstants.declaration]: false,
	Amount: 0,
};

interface IInitialValues {
	[x: string]: any;
}

const useComponentDidMount = (func) => useEffect(func, []);

const getPaymentRecipient = (
	defaultPaymentDestination: string,
	contentId: string,
	setPaymentRecipient: React.Dispatch<React.SetStateAction<IPaymentRecipient>>
) => {
	try {
		fetch(`/Umbraco/Api/FinanceService/GetCampaignList?contentId=${contentId}`, {
			method: "GET",
		})
			.then((resp) => resp.json())
			.then((data) => {
				if (!isEmpty(data) && data.data != undefined && !isEmpty(data.data)) {
					const campaignList = data.data as ICampaign[];
					if (
						defaultPaymentDestination &&
						campaignList.find((c) => c.website_name.trim() == defaultPaymentDestination)
					) {
						setPaymentRecipient({
							account: campaignList.find(
								(c) => c.website_name.trim() == defaultPaymentDestination
							).account,
							campaignCode: campaignList.find(
								(c) => c.website_name.trim() == defaultPaymentDestination
							).code,
						});
					}
				}
			});
	} catch (e) {
		alert(`Fail to match campaign name.`);
	}
};

interface IEventFormProps extends IStandardFormProps {
	setIsSubmitted: React.Dispatch<React.SetStateAction<boolean>>;
	defaultPaymentDestination?: string;
	paypalClientId: string;
	rootNode: string;
	declarationText: string;
}

interface IPaymentRecipient {
	account: string;
	campaignCode: string;
}

export const EventForm = (props: IEventFormProps) => {
	const isWaForm = props.rootNode == "2";
	const [paymentRecipient, setPaymentRecipient] = useState<IPaymentRecipient>({
		account: "",
		campaignCode: "",
	});

	useComponentDidMount(() =>
		getPaymentRecipient(props.defaultPaymentDestination, props.contentId, setPaymentRecipient)
	);
	const { executeRecaptcha } = useGoogleReCaptcha();
	const handleResponse = async (
		resp: any,
		actions: FormikActions<any>,
		setIsSubmitted: React.Dispatch<React.SetStateAction<boolean>>
	) => {
		const jsonRes = await resp.json();
		if (jsonRes.Success === false || !resp.ok) {
			setSubmitError(
				`${jsonRes.Message !== undefined ? jsonRes.Message : ""} ${
					resp !== undefined && !resp.ok ? jsonRes : ""
				}`
			);
			actions.setSubmitting(false);
		} else {
			setSubmitDisabled(true);
			setIsSubmitted(true);
			actions.resetForm();
			if (!isNullOrWhiteSpace(props.redirectUrl) && props.modalEnabled === false) {
				if (props.redirectTarget == "_blank") {
					window.open(props.redirectUrl, props.redirectTarget);
				} else {
					window.location.href = props.redirectUrl;
				}
			} else {
				if (props.modalEnabled) {
					setShowModal(true);
				}
				setSubmitError("Your form has been successfully submitted. Thank you.");
			}
		}
	};

	let submitFunction = async (
		values: EventRegFormModel,
		actions: FormikActions<EventRegFormModel>,
		paymentRecipient: IPaymentRecipient,
		setIsSubmitted: React.Dispatch<React.SetStateAction<boolean>>
	) => {
		const rcToken = getRcTokenAsync("eventReg", executeRecaptcha);
		const fields = [
			...EventFormFields,
			...(isWaForm && values[WaFormConstants.entityType] === ENTITY_TYPE.Company
				? stepOneCompanyFields
				: StandardFormFields),
		];
		const fixedValues = separateCustomFormFields(values, fields, props.fields, isWaForm);
		fixedValues["eventId"] = props.eventId;
		if (values[FormConstants.numberOfTickets] === 0 && props.maximumTicketsPerReg < 2) {
			fixedValues[FormConstants.numberOfTickets] = 1;
		}
		if (!isNullOrWhiteSpace(values[FormConstants.paypalTransactionId])) {
			fixedValues["ListOfExtraFields"][FormConstants.paypalTransactionId] =
				values[FormConstants.paypalTransactionId];
		}
		const ContentId = parseInt(props.contentId, 10);
		let body = { ContentId: ContentId, ...fixedValues };

		if (props.paymentRequired) {
			if (generateTokenRef.current) {
				if (fixedValues["PaymentProvider"] === "paypal") {
					body = {
						ContentId,
						...fixedValues,
						rcToken: await tokenFailable(rcToken),
					};
				} else {
					body = await generateTokenRef.current(
						ContentId,
						fixedValues,
						actions,
						paymentRecipient.account,
						rcToken
					);
				}
			} else {
				setSubmitError(message.tokenError);
				actions.setSubmitting(false);
			}
		}

		if (!props.paymentRequired || (props.paymentRequired && !body === false)) {
			await fetch("/Umbraco/Api/EventRegForm/Submit", {
				method: "POST",
				headers: {
					Accept: "application/json",
					"Content-Type": "application/json",
				},
				body: JSON.stringify({ ...body, Campaign: paymentRecipient.campaignCode }),
			}).then((resp) => {
				handleResponse(resp, actions, setIsSubmitted);
			});
		} else {
			setSubmitError(message.tokenError);
			actions.setSubmitting(false);
		}
	};
	const [submitError, setSubmitError] = React.useState("");
	const [showModal, setShowModal] = React.useState(false);
	const [submitDisabled, setSubmitDisabled] = React.useState(false);
	const generateTokenRef =
		React.useRef<
			(
				contentId: number,
				fixedValues: any,
				actions: FormikActions<any>,
				account: string,
				recaptChaToken: Promise<IRcTokenResponse>
			) => void
		>();

	const updateTokenGenerator = (func: () => Promise<any>) => {
		generateTokenRef.current = func;
	};

	// what is this? ahh so props.fields are the custom fields that may be specified
	// here we are adding in something to the initial values
	// this does this only once
	const [formikInitial] = React.useState(() => {
		const initial = { ...initialValues };
		Object.entries(props.fields).forEach(([key, value]) => {
			if (value.CustomFieldType == "checkbox") {
				initial[removeWhiteSpaces(value.Name)] = false;
			} else {
				initial[removeWhiteSpaces(value.Name)] = "";
			}
		});
		if (props.commonFieldsMeta.State.Show) {
			initial[FormConstants.state] = "";
		}
		return initial;
	});

	return (
		<div className="genWrapper">
			<Formik
				initialValues={formikInitial}
				validationSchema={createGenericValidationSchema({
					commonFieldsMeta: props.commonFieldsMeta,
					fields: props.fields,
					useStoredData: props.autocomplete,
					declaration:
						(props.declarationText !== undefined &&
							props.declarationText !== "" &&
							props.declarationText !== null) ||
						isWaForm,
				})}
				onSubmit={(values: EventRegFormModel, actions: FormikActions<EventRegFormModel>) =>
					submitFunction(values, actions, paymentRecipient, props.setIsSubmitted)
				}
				render={({
					errors,
					touched,
					values,
					setFieldValue,
					isValid,
					isSubmitting,
					setFieldTouched,
					submitForm,
				}: FormikProps<EventRegFormModel>) => {
					return (
						<InnerEventForm
							{...props}
							isSubmitting={isSubmitting}
							errors={errors}
							touched={touched}
							values={values}
							setFieldValue={setFieldValue}
							showModal={showModal}
							setShowModal={setShowModal}
							submitDisabled={submitDisabled}
							submitError={submitError}
							updateTokenGenerator={updateTokenGenerator}
							setFieldTouched={setFieldTouched}
							submitForm={submitForm}
							isWaForm={isWaForm}
							isValid={isValid}
						/>
					);
				}}
			/>
			{props.totalTickets === 0 || props.ticketsLeft > 0 ? (
				<></>
			) : (
				<p>
					** Registration is now closed as the event has reached full capacity. Thank you
					for your interest.**
				</p>
			)}
		</div>
	);
};

function InnerEventForm(
	props: IEventFormProps & {
		isSubmitting: boolean;
		errors: FormikErrors<EventRegFormModel>;
		touched: FormikTouched<EventRegFormModel>;
		values: EventRegFormModel;
		setFieldValue: (field: string, value: any) => void;
		setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void;
		showModal: boolean;
		setShowModal: (showModal: boolean) => void;
		submitDisabled: boolean;
		submitError: string;
		updateTokenGenerator: (func: () => Promise<any>) => void;
		submitForm: () => void;
		paypalClientId: string;
		isWaForm: boolean;
		isValid: boolean;
		declarationText: string;
	}
) {
	const {
		touched,
		values,
		setFieldValue,
		isSubmitting,
		errors,
		showModal,
		setShowModal,
		submitDisabled,
		submitError,
		updateTokenGenerator,
		setFieldTouched,
		submitForm,
		paypalClientId,
		autocomplete,
		isWaForm,
		isValid,
		declarationText,
	} = props;
	const isWaCompany = isWaForm && values[WaFormConstants.entityType] === ENTITY_TYPE.Company;
	const [stripeError, setStripeError] = React.useState("");
	const [stripeComplete, setStripeComplete] = React.useState(false);
	const [hasSubmit, setHasSubmit] = React.useState<boolean>(false);

	const {
		loading: isFetchingEventFormData,
		autocompletedFields,
		setAutocompletedFields,
	} = useAutoComplete<EventRegFormModel>({
		autocompleteType: AUTOCOMPLETE_FORM_TYPE.EventForm,
		emailAddress: values[FormConstants.email],
		contactEmailAddress: values[WaFormConstants.contactEmail],
		entityType: isWaCompany ? ENTITY_TYPE.Company : ENTITY_TYPE.Individual,
		setFieldValue,
		setFieldTouched,
		skip: !autocomplete,
		errors,
	});
	const autoCompleted = autocompletedFields
		? Object.values(autocompletedFields).some((value) => value === true)
		: false;

	return (
		<Form hidden={props.totalTickets > 0 && props.ticketsLeft < 1}>
			<Modal
				showModal={showModal}
				setShowModal={setShowModal}
				shareUrl={props.shareUrl}
				shareTitle={props.shareTitle}
				shareMessage={props.shareMessage}
				shareHashTag={props.shareHashTag}
				modalTitle={props.modalTitle}
				modalMessage={props.modalMessage}
				colorTheme={props.colorTheme}
				modalImageUrl={props.modalImageUrl}
			/>
			<div className="eventForm">
				<div className="contentWrapper">
					<div
						className={
							`${props.disableGradient ? "" : "genGradient"} ` + props.colorTheme
						}
					>
						<div className="container">
							{props.formHeader ? (
								<p className="event-form-header">{props.formHeader}</p>
							) : null}
							{props.headerSubtext ? (
								<p className="event-form-subheader">{props.headerSubtext}</p>
							) : null}
							<hr />
							{isWaForm ? (
								<div className="container">
									<div className="row">
										<Field
											className={"genOpposite" + props.colorTheme}
											component="div"
											style={{ display: "flex" }}
											name={WaFormConstants.entityType}
										>
											<div
												style={{ textAlign: "left", marginLeft: "20px" }}
												className="form-check"
											>
												<input
													defaultChecked={
														props.values[WaFormConstants.entityType] ===
														"individual"
													}
													name={WaFormConstants.entityType}
													type="radio"
													value="individual"
													className="form-check-input"
													id="individual"
													style={{ cursor: "pointer" }}
												/>
												<label
													className="form-check-label"
													style={{ cursor: "pointer" }}
													htmlFor="individual"
												>
													I attend as an individual
												</label>
											</div>
											<div
												style={{ textAlign: "left", marginLeft: "20px" }}
												className="form-check"
											>
												<input
													defaultChecked={
														props.values[WaFormConstants.entityType] ===
														ENTITY_TYPE.Company
													}
													name={WaFormConstants.entityType}
													type="radio"
													value="company"
													className="form-check-input"
													id="company"
													style={{ cursor: "pointer" }}
												/>
												<label
													className="form-check-label"
													style={{ cursor: "pointer" }}
													htmlFor="company"
												>
													I attend on behalf of an association or company
												</label>
											</div>
										</Field>
									</div>
								</div>
							) : null}
							{props.autocomplete ? (
								<>
									{isWaCompany ? (
										<div className="row">
											<FormField
												fieldClassName="form-control standard-form-group"
												type="text"
												name={WaFormConstants.contactEmail}
												placeholder={unCamelCase(
													WaFormConstants.contactEmail
												)}
												errors={props.errors}
												touched={props.touched}
												colorTheme={props.colorTheme}
												isColumn={props.fieldsAsColumn}
												setFieldValue={setFieldValue}
											/>
										</div>
									) : (
										<FormFields
											errors={errors}
											values={values}
											touched={touched}
											setFieldValue={setFieldValue}
											colorTheme={props.colorTheme}
											fields={[]}
											isColumn={props.fieldsAsColumn || props.narrowForm}
											commonFieldsMeta={{
												Email: { Show: true, Required: true },
												FirstName: { Required: false, Show: false },
												LastName: { Required: false, Show: false },
												Postcode: { Required: false, Show: false },
												State: { Required: false, Show: false },
											}}
										/>
									)}
									{autoCompleted ? (
										<p
											className={"col-md-12 donOpposite" + props.colorTheme}
											style={{
												marginBottom: 0,
												marginTop: 10,
												textAlign: "center",
											}}
										>
											Welcome back! If your contact details have changed since
											the last time you attended our events,{" "}
											<label
												style={{
													textDecoration: "underline",
													cursor: "pointer",
												}}
												onClick={() => {
													setAutocompletedFields(undefined);
													props.setFieldValue(FormConstants.address, "");
													props.setFieldValue(
														FormConstants.firstName,
														""
													);
													props.setFieldValue(FormConstants.lastName, "");
													props.setFieldValue(FormConstants.phone, "");
													props.setFieldValue(FormConstants.postcode, "");
													props.setFieldValue(FormConstants.state, "");
													props.setFieldValue(FormConstants.suburb, "");
													props.setFieldValue(FormConstants.mobile, "");
													props.setFieldValue(
														WaFormConstants.companyName,
														""
													);
													props.setFieldValue(WaFormConstants.abn, "");
													props.setFieldValue(
														WaFormConstants.companyAddress,
														""
													);
													props.setFieldValue(
														WaFormConstants.companySuburb,
														""
													);
													props.setFieldValue(
														WaFormConstants.companyState,
														""
													);
													props.setFieldValue(
														WaFormConstants.companyPostcode,
														""
													);
													props.setFieldValue(
														WaFormConstants.contactFirstName,
														""
													);
													props.setFieldValue(
														WaFormConstants.contactLastName,
														""
													);
													props.setFieldValue(
														WaFormConstants.contactPhone,
														""
													);
												}}
											>
												click here to update them.
											</label>
										</p>
									) : null}
								</>
							) : null}
							<LoadingOverlay
								active={isFetchingEventFormData}
								className={"loadingOverlay"}
								spinner={<ClipLoader sizeUnit={"px"} size={20} color={"white"} />}
							>
								{isWaForm &&
								props.values[WaFormConstants.entityType] === ENTITY_TYPE.Company ? (
									<div className="row">
										{" "}
										{stepOneCompanyFields
											.filter((field) => {
												if (
													autocompletedFields &&
													field in autocompletedFields
												) {
													return !autocompletedFields[field];
												} else {
													return isWaCompany && autocomplete
														? field !== WaFormConstants.contactEmail
														: true;
												}
											})
											.map((f, i) => {
												if (f === WaFormConstants.companyState) {
													return (
														<FormField
															key={i}
															colorTheme={props.colorTheme}
															isColumn={props.fieldsAsColumn}
															type="text"
															fieldClassName="form-control standard-form-group"
															name={WaFormConstants.companyState}
															component="select"
															placeholder="State*"
															errors={props.errors}
															touched={props.touched}
															children={States.map((opt, index) => (
																<option
																	key={index}
																	value={opt.value}
																>
																	{opt.text}
																</option>
															))}
															setFieldValue={setFieldValue}
														/>
													);
												} else {
													return (
														<FormField
															key={i}
															fieldClassName="form-control standard-form-group"
															type="text"
															name={f}
															placeholder={unCamelCase(f)}
															errors={props.errors}
															touched={props.touched}
															colorTheme={props.colorTheme}
															isColumn={props.fieldsAsColumn}
															setFieldValue={setFieldValue}
														/>
													);
												}
											})}
									</div>
								) : (
									<FormFields
										errors={errors}
										values={values}
										touched={touched}
										setFieldValue={setFieldValue}
										colorTheme={props.colorTheme}
										fields={
											autoCompleted
												? props.fields?.filter((f) => {
														if (
															autocompletedFields &&
															f.Name in autocompletedFields
														) {
															return !autocompletedFields[f.Name];
														} else {
															return true;
														}
												  })
												: props.fields
										}
										isColumn={props.fieldsAsColumn || props.narrowForm}
										commonFieldsMeta={{
											Email: {
												Show: autocomplete
													? false
													: props.commonFieldsMeta.Email.Show,
												Required: autocomplete
													? false
													: props.commonFieldsMeta.Email.Required,
											},
											FirstName: {
												Required: props.commonFieldsMeta.FirstName.Required,
												Show: autoCompleted
													? autocompletedFields?.FirstName
														? false
														: props.commonFieldsMeta.FirstName.Show
													: props.commonFieldsMeta.FirstName.Show,
											},
											LastName: {
												Required: props.commonFieldsMeta.LastName.Required,
												Show: autoCompleted
													? autocompletedFields?.LastName
														? false
														: props.commonFieldsMeta.LastName.Show
													: props.commonFieldsMeta.LastName.Show,
											},
											Postcode: {
												Required: props.commonFieldsMeta.Postcode.Required,
												Show: autoCompleted
													? autocompletedFields?.Postcode
														? false
														: props.commonFieldsMeta.Postcode.Show
													: props.commonFieldsMeta.Postcode.Show,
											},
											State: {
												Required: props.commonFieldsMeta.State.Required,
												Show: autoCompleted
													? autocompletedFields?.State
														? false
														: props.commonFieldsMeta.State.Show
													: props.commonFieldsMeta.State.Show,
											},
										}}
									/>
								)}
							</LoadingOverlay>

							<FormCheckOutComponent
								setFieldValue={setFieldValue}
								contentId={props.contentId}
								paymentProvider={props.paymentProvider}
								stripe={props.stripe}
								updateTokenGenerator={updateTokenGenerator}
								maximumTicketsPerReg={props.maximumTicketsPerReg}
								totalTickets={props.totalTickets}
								ticketsLeft={props.ticketsLeft}
								errors={errors}
								touched={touched}
								colorTheme={props.colorTheme}
								paymentRequired={props.paymentRequired}
								ticketCost={props.ticketCost}
								values={values}
								paymentError={stripeError}
								setPaymentError={setStripeError}
								setPaymentComplete={setStripeComplete}
								isColumn={props.fieldsAsColumn || props.narrowForm}
								isEventReg={true}
								submitDisabled={
									(props.maximumTicketsPerReg > 1 &&
										values[FormConstants.numberOfTickets] === 0) ||
									isSubmitting ||
									!isValid ||
									submitDisabled ||
									Object.keys(touched)?.length === 0
								}
								isSubmitting={isSubmitting}
								submitForm={submitForm}
								paypalClientId={paypalClientId}
							/>
						</div>
						{hasSubmit && !submitDisabled && (
							<div className="genErrorMessages">
								{Object.keys(values).map((key: any, index: any) =>
									touched[key] && errors[key] ? (
										<ErrorMessage
											errors={errors}
											touched={touched}
											colorTheme={props.colorTheme}
											name={key}
											key={index}
										/>
									) : null
								)}
							</div>
						)}
						{declarationText || isWaForm ? (
							<div className="container">
								<div
									style={{ textAlign: "center" }}
									className={`checkbox-group-wrapper my-2 px-4 col-md-12 ${
										props.touched[FormConstants.declaration] &&
										props.errors[FormConstants.declaration]
											? "error-validation-" + props.colorTheme
											: ""
									}`}
								>
									<Checkbox
										onChange={(e) =>
											setFieldValue(
												FormConstants.declaration,
												e.target.checked
											)
										}
										name={FormConstants.declaration}
									>
										{isWaForm
											? "I confirm that I am an Australian citizen or resident and not otherwise prohibited from contributing to a political party under Australian or Western Australian electoral law"
											: declarationText}
									</Checkbox>
								</div>
							</div>
						) : (
							<></>
						)}
						<div className="container">
							<FormButton
								colorTheme={props.colorTheme}
								errors={errors}
								touched={touched}
								buttonText={props.buttonText}
								buttonRoundedCorners={props.buttonRoundedCorners}
								isEventForm={true}
								buttonColorTheme={props.buttonColorTheme}
								setHasSubmit={setHasSubmit}
								submitDisabled={
									(props.maximumTicketsPerReg > 1 &&
										values[FormConstants.numberOfTickets] === 0) ||
									(props.paymentRequired
										? stripeError !== "" ||
										  !stripeComplete ||
										  (isSubmitting ? true : submitDisabled)
										: isSubmitting
										? true
										: submitDisabled)
								}
							/>
							{submitError ? (
								<div className="SubmitErrorMessage">{submitError}</div>
							) : null}
						</div>
					</div>
				</div>
			</div>
		</Form>
	);
}

export default injectStripe(EventForm);
